import { memo } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { useNavigate } from "react-router-dom";
import { slugify } from "@/lib/locations";

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const TARGET_STATES = ["Kentucky", "Tennessee", "Ohio", "Indiana"];

const CITIES = [
  {
    name: "Louisville",
    coordinates: [-85.7585, 38.2527],
    dy: -10,
    dx: 0,
    anchor: "middle",
  },
  {
    name: "Lexington",
    coordinates: [-84.5037, 38.0406],
    dy: 15,
    dx: 0,
    anchor: "middle",
  },
  {
    name: "Columbus",
    coordinates: [-82.9988, 39.9612],
    dy: -10,
    dx: 0,
    anchor: "middle",
  },
  {
    name: "Cincinnati",
    coordinates: [-84.512, 39.1031],
    dy: 5,
    dx: -10,
    anchor: "end",
  },
  {
    name: "Dayton",
    coordinates: [-84.1916, 39.7589],
    dy: 5,
    dx: -10,
    anchor: "end",
  },
  {
    name: "Indianapolis",
    coordinates: [-86.1581, 39.7684],
    dy: -10,
    dx: 0,
    anchor: "middle",
  },
  {
    name: "Evansville",
    coordinates: [-87.5711, 37.9716],
    dy: 15,
    dx: 0,
    anchor: "middle",
  },
  {
    name: "Fort Wayne",
    coordinates: [-85.1394, 41.0793],
    dy: -10,
    dx: 0,
    anchor: "middle",
  },
  {
    name: "Nashville",
    coordinates: [-86.7816, 36.1627],
    dy: -10,
    dx: 0,
    anchor: "middle",
  },
  {
    name: "Knoxville",
    coordinates: [-83.9207, 35.9606],
    dy: 15,
    dx: 0,
    anchor: "middle",
  },
  {
    name: "Memphis",
    coordinates: [-90.049, 35.1495],
    dy: -10,
    dx: 0,
    anchor: "middle",
  },
];

const InteractiveMap = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full h-full min-h-[400px] flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 p-4 overflow-hidden">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          center: [-84.5, 38.5],
          scale: 3200,
        }}
        className="w-full h-full max-h-[500px]"
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const isTarget = TARGET_STATES.includes(geo.properties.name);

              if (!isTarget) {
                // Return non-target states with a subtle color, or we could just not render them
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#F3F4F6"
                    stroke="#E5E7EB"
                    strokeWidth={0.5}
                    className="outline-none"
                  />
                );
              }

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onClick={() =>
                    navigate(`/service-areas/${slugify(geo.properties.name)}`)
                  }
                  fill="#f97316" // salt-orange
                  stroke="#ffffff"
                  strokeWidth={1.5}
                  className="outline-none cursor-pointer transition-all duration-300"
                  style={{
                    default: { outline: "none", fill: "#f97316" },
                    hover: {
                      outline: "none",
                      fill: "#f97316", // salt-orange
                      transform: "scale(1.02)",
                      transformOrigin: "center center",
                      filter: "drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.2))",
                      zIndex: 10,
                    },
                    pressed: { outline: "none", fill: "#f97316" },
                  }}
                />
              );
            })
          }
        </Geographies>
        {CITIES.map(({ name, coordinates, dx, dy, anchor }) => (
          <Marker key={name} coordinates={coordinates as [number, number]}>
            <circle r={3} fill="#ffffff" stroke="#111827" strokeWidth={1.5} />
            <text
              textAnchor={anchor as "start" | "middle" | "end"}
              y={dy}
              x={dx}
              style={{
                fontFamily: "Inter, sans-serif",
                fill: "#ffffff",
                fontSize: "12px",
                fontWeight: 700,
                pointerEvents: "none",
              }}
              stroke="#111827"
              strokeWidth={3}
              strokeLinejoin="round"
              paintOrder="stroke"
            >
              {name}
            </text>
          </Marker>
        ))}
      </ComposableMap>
    </div>
  );
};

export default memo(InteractiveMap);
